import React from "react";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { forEach } from "lodash";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { MrbSelectStore, MrbSelect } from "mrb/components/select";
import { localizationService } from "common/localization";

const CLEAR_OPTION_VALUE = "__clear__";
const PROFILE_PAGE_SIZE = 20;

class LoadioDeviceListViewStore extends MrbBaseListViewStore {
    @observable updatingDeviceIds = new Map();
    @observable selectedDeviceIds = new Map();
    @observable isBatchSaving = false;
    @observable.ref batchProfileSelectStore = null;
    _selectStoresByDevice = new Map();

    @computed get selectedCount() {
        return this.selectedDeviceIds.size;
    }

    @computed get hasSelection() {
        return this.selectedDeviceIds.size > 0;
    }

    @computed get canApplyBatch() {
        return (
            this.hasSelection &&
            !this.isBatchSaving &&
            this.batchProfileSelectStore &&
            this.batchProfileSelectStore.value != null
        );
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {},
        });
        makeObservable(this);
        this.routeStore = routeStore;

        this.batchProfileSelectStore = this._buildBatchProfileSelectStore();

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                mapper: (item) => ({
                    ...item,
                    id: item.deviceId,
                    fullName: `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.displayName || "-",
                    phone:
                        item.phoneNumber && item.phoneCountryCode
                            ? `+${item.phoneCountryCode} ${item.phoneNumber}`
                            : item.phoneNumber || "-",
                }),
                columns: [
                    {
                        key: "fullName",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.COLUMNS.OWNER",
                        sortable: false,
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                    },
                    {
                        key: "phone",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.COLUMNS.PHONE",
                    },
                    {
                        key: "email",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.COLUMNS.EMAIL",
                    },
                    {
                        key: "deviceName",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.COLUMNS.DEVICE_NAME",
                        sortable: true,
                    },
                    {
                        key: "loadioContentProfileId",
                        title: "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.COLUMNS.PROFILE",
                        cell: (params) => this._renderProfileSelect(params),
                        minWidth: 280,
                    },
                ],
                selection: {
                    rowSelection: "multiple",
                    isRowSelectable: () => true,
                    onRowSelected: (node) => {
                        const id = node.data?.deviceId;
                        if (!id) return;
                        if (node.isSelected() && !this.selectedDeviceIds.has(id)) {
                            this.toggleDevice(id, true, node.data);
                        } else if (!node.isSelected() && this.selectedDeviceIds.has(id)) {
                            this.toggleDevice(id, false, node.data);
                        }
                    },
                },
                onDataChange: () => {
                    const api = this.gridStore.gridApi;
                    if (!api) return;
                    const rowsToSelect = [];
                    api.forEachNode((node) => {
                        if (node.data && this.selectedDeviceIds.has(node.data.deviceId)) {
                            rowsToSelect.push(node);
                        }
                    });
                    forEach(rowsToSelect, (node) => api.selectNode(node, true));
                },
            })
        );
    }

    async find(params) {
        return this.routeStore.findDevices(params);
    }

    _buildBatchProfileSelectStore() {
        return new MrbSelectStore({
            isSearchable: true,
            isClearable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            pageSize: PROFILE_PAGE_SIZE,
            placeholder: localizationService.t(
                "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.BATCH.PROFILE_PLACEHOLDER"
            ),
            actions: {
                find: (searchTerm, pageNumber, pageSize) => this._fetchProfilesWithClear(searchTerm, pageNumber, pageSize),
            },
        });
    }

    async _fetchProfilesWithClear(searchTerm, pageNumber, pageSize) {
        const page = await this.routeStore.findProfiles({
            searchQuery: searchTerm,
            page: pageNumber,
            rpp: pageSize,
            sort: "name|asc",
        });
        const items = page?.item || [];
        const includeClear = pageNumber === 1 && !searchTerm;
        const clearOption = {
            id: CLEAR_OPTION_VALUE,
            name: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.BATCH.CLEAR_OPTION"),
        };
        const combined = includeClear ? [clearOption, ...items] : items;
        return {
            item: combined,
            page: page?.page ?? pageNumber,
            recordsPerPage: page?.recordsPerPage ?? pageSize,
            totalRecords: (page?.totalRecords ?? items.length) + (includeClear ? 1 : 0),
        };
    }

    @action.bound
    toggleDevice(id, checked, item) {
        if (!id) return;
        if (checked) {
            this.selectedDeviceIds.set(id, item);
        } else {
            this.selectedDeviceIds.delete(id);
        }
    }

    @action.bound
    clearSelection() {
        this.selectedDeviceIds.clear();
        const api = this.gridStore && this.gridStore.gridApi;
        if (api) api.deselectAll();
    }

    @action.bound
    async applyBatchProfile() {
        if (!this.canApplyBatch) return;
        const value = this.batchProfileSelectStore.value;
        const profileId = value === CLEAR_OPTION_VALUE ? null : value;
        const ids = Array.from(this.selectedDeviceIds.keys());
        runInAction(() => (this.isBatchSaving = true));
        try {
            await this.routeStore.batchAssignProfile(ids, profileId);
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.BATCH.SUCCESS", {
                    count: ids.length,
                })
            );
            this.batchProfileSelectStore.clear();
            this.clearSelection();
            this.queryUtility.fetch();
            this._selectStoresByDevice.clear();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.BATCH.ERROR"),
                err
            );
        } finally {
            runInAction(() => (this.isBatchSaving = false));
        }
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error("An unexpected error occurred while trying to load devices.", err);
    }

    _getSelectStore = (device) => {
        const existing = this._selectStoresByDevice.get(device.deviceId);
        if (existing) {
            return existing;
        }
        const initialItems =
            device.loadioContentProfileId && device.loadioContentProfileName
                ? [{ id: device.loadioContentProfileId, name: device.loadioContentProfileName }]
                : null;
        const store = new MrbSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                isClearable: true,
                initFetch: false,
                textKey: "name",
                valueKey: "id",
                pageSize: PROFILE_PAGE_SIZE,
                placeholder: localizationService.t("ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.NONE_OPTION"),
                actions: {
                    find: (searchTerm, pageNumber, pageSize) =>
                        this.routeStore.findProfiles({
                            searchQuery: searchTerm,
                            page: pageNumber,
                            rpp: pageSize,
                            sort: "name|asc",
                        }),
                    onChange: (option) => {
                        const newId = option ? option.value : null;
                        this.assignProfile(device, newId);
                    },
                },
            },
            initialItems
        );
        if (initialItems) {
            store.setSelectedItem(initialItems[0]);
        }
        this._selectStoresByDevice.set(device.deviceId, store);
        return store;
    };

    _renderProfileSelect = (params) => {
        const device = params.data;
        if (!device) return null;
        const store = this._getSelectStore(device);
        return (
            <MrbSelect
                store={store}
                classNameExtend="c-dropdown--dark c-dropdown--sml"
                menuPortalTarget={document.body}
                styles={{
                    control: (base) => ({ ...base, flex: 1 }),
                    valueContainer: (base) => ({ ...base, overflow: "visible" }),
                    singleValue: (base) => ({ ...base, overflow: "visible" }),
                }}
            />
        );
    };

    @action.bound
    async assignProfile(device, profileId) {
        const normalized = profileId === "" || profileId == null ? null : profileId;
        if ((device.loadioContentProfileId || null) === normalized) return;
        const previousId = device.loadioContentProfileId || null;
        const previousName = device.loadioContentProfileName || null;
        const store = this._selectStoresByDevice.get(device.deviceId);
        const newName = store?.selectedItem?.name || null;
        this.updatingDeviceIds.set(device.deviceId, true);
        try {
            await this.routeStore.assignProfile(device.deviceId, normalized);
            runInAction(() => {
                device.loadioContentProfileId = normalized;
                device.loadioContentProfileName = normalized ? newName : null;
            });
            this.rootStore.notificationStore.success(
                localizationService.t(
                    normalized
                        ? "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.ASSIGN_SUCCESS"
                        : "ADMINISTRATION.LOADIO_DASHBOARD.DEVICES.CLEAR_SUCCESS"
                )
            );
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while assigning the content profile.",
                err
            );
            if (store) {
                if (previousId && previousName) {
                    store.setSelectedItem({ id: previousId, name: previousName });
                } else {
                    store.clear?.();
                }
            }
        } finally {
            runInAction(() => {
                this.updatingDeviceIds.delete(device.deviceId);
            });
        }
    }
}

export default LoadioDeviceListViewStore;
